import { PersonalNote, PersonalNoteTargetType } from "../models/personalNote";
import { RestService, Entity } from "./restService";

export type CreatePersonalNoteInput = Pick<
    PersonalNote,
    "targetType" | "targetId" | "content"
>;

const normalizePersonalNotes = (payload: unknown): PersonalNote[] =>
    Array.isArray(payload) ? payload : [];

/**
 * Service for managing personal notes (user-private notes on meeting items)
 * Personal notes are only visible to the creator
 */
export class PersonalNoteService extends RestService<PersonalNote & Entity> {
    constructor(baseUrl: string) {
        super(baseUrl, "/personalNotes");
    }

    /**
     * Get all private notes for the current user within a meeting workflow.
     */
    async getNotesForMeeting(meetingId: string): Promise<PersonalNote[]> {
        const realClient = await this.axiosFactory();
        const response = await realClient.get(`/meeting/${meetingId}`);
        return normalizePersonalNotes(response.data);
    }

    /**
     * Get personal notes for a specific target visible to the current user.
     */
    async getNotesForTarget(
        targetType: PersonalNoteTargetType,
        targetId: string,
    ): Promise<PersonalNote[]> {
        const realClient = await this.axiosFactory();
        const response = await realClient.get(`/target/${targetType}/${targetId}`);
        return normalizePersonalNotes(response.data);
    }

    /**
     * Create a new personal note
     */
    async createNote(note: CreatePersonalNoteInput): Promise<PersonalNote> {
        const realClient = await this.axiosFactory();
        const response = await realClient.post("", note);
        return response.data;
    }

    /**
     * Update an existing personal note (owner only)
     */
    async updateNote(note: PersonalNote): Promise<PersonalNote> {
        const realClient = await this.axiosFactory();
        const response = await realClient.put(`/${note.id}`, note);
        return response.data;
    }

    /**
     * Delete a personal note (owner only)
     */
    async deleteNote(noteId: string): Promise<void> {
        const realClient = await this.axiosFactory();
        await realClient.delete(`/${noteId}`);
    }
}

// Factory function to create PersonalNoteService
export const createPersonalNoteService = (baseUrl: string): PersonalNoteService => {
    return new PersonalNoteService(baseUrl);
};
